import { Section } from '@/common/types/lessonPlan';
import {
  ActivityType,
  lookupExampleWord,
  parseHTMLToSections,
  WordMatrixData,
} from './utils';

const extractTextContent = (html: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

const generateActivityBlockHTML = (section: Section): string => {
  let html = `
    <div class="activity-block" data-type="${section.type}" data-id="${section.id}">
      ${createHeading(2, section.title)}
  `;

  switch (section.type) {
    case ActivityType.SOUND_RECOGNITION_DRILL:
      html += generateSoundRecognitionContent(section);
      break;
    case ActivityType.LETTER_SOUND_SPELLING_DRILL:
      html += generateWorksheetLinesContent(5);
      break;
    case ActivityType.ORAL_SEGMENTING_DRILL:
      html += generateOralInstructionContent();
      break;
    case ActivityType.ORAL_BLENDING_DRILL:
      html += generateOralInstructionContent();
      break;
    case ActivityType.WORD_READING:
      html += generateHorizontalContent(section);
      break;
    case ActivityType.DECODING_WORD_CHAIN:
      html += generateWordChainContent(section);
      break;
    case ActivityType.SPELLING_WORD_CHAIN:
      html += generateWorksheetLinesContent(5);
      break;
    case ActivityType.SENTENCE_READING:
      html += generateVerticalListContent(section);
      break;
    case ActivityType.SENTENCE_DICTATION:
      html += generateWorksheetLinesContent(5);
      break;
    case ActivityType.WORD_SUMS:
      html += generateWordSumsContent(section);
      break;
    case ActivityType.SPELLING_DRILL:
      html += generateWorksheetLinesContent(5);
      break;
    case ActivityType.WORD_MATRIX:
      html += generateWordMatrixContent(section);
      break;
    default:
      console.warn(`Unhandled activity type: ${section.type}`);
  }

  html += `</div>`;
  return html;
};

const parseSectionsFromElement = (element: Element): Section[] => {
  const tempDiv = document.createElement('div');
  tempDiv.appendChild(element.cloneNode(true));
  return parseHTMLToSections(tempDiv.innerHTML);
};

export const generateStimulusSheetContent = (content: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');

  let stimulusSheetContent = `
    <h1>Student Worksheet</h1>
    <p class="description">Use alongside lesson plan</p>
  `;

  doc.body.childNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;

    if (node.classList.contains('column-layout')) {
      const columns = node.querySelectorAll(':scope > .column');
      if (columns.length >= 2) {
        let columnLayoutHTML = '<div class="column-layout">';
        columns.forEach((col) => {
          let columnHTML = '<div class="column">';
          col.childNodes.forEach((child) => {
            if (!(child instanceof HTMLElement)) return;
            if (child.classList.contains('activity-block')) {
              const sections = parseSectionsFromElement(child);
              sections.forEach((section) => {
                if (section.type !== 'custom') {
                  columnHTML += generateActivityBlockHTML(section);
                }
              });
            }
          });
          columnHTML += '</div>';
          columnLayoutHTML += columnHTML;
        });
        columnLayoutHTML += '</div>';
        stimulusSheetContent += columnLayoutHTML;
      }
    } else if (node.classList.contains('activity-block')) {
      const sections = parseSectionsFromElement(node);
      sections.forEach((section) => {
        if (section.type !== 'custom') {
          stimulusSheetContent += generateActivityBlockHTML(section);
        }
      });
    }
  });

  return stimulusSheetContent;
};

const createHeading = (level: number, content: string) =>
  `<h${level}>${content}</h${level}>`;

const generateHorizontalContent = (section: Section): string => {
  const items = section.content
    .map(
      (item) =>
        `<span class="content-item">${extractTextContent(
          item.value as string,
        )}</span>`,
    )
    .join(', ');
  return `<div class="content horizontal-layout">${items}</div>`;
};

const generateSoundRecognitionContent = (section: Section): string => {
  const items = section.content
    .map((item) => {
      const raw = extractTextContent(item.value as string);
      const pipeIdx = raw.indexOf('|');
      const spelling = pipeIdx >= 0 ? raw.substring(0, pipeIdx) : raw;
      const pronunciation =
        pipeIdx >= 0 ? raw.substring(pipeIdx + 1) : undefined;
      const exampleWord = lookupExampleWord(spelling, pronunciation);
      const label = exampleWord
        ? `<strong>${spelling}</strong> for ${exampleWord}`
        : `<strong>${spelling}</strong>`;
      return `<li>${label}</li>`;
    })
    .join('');
  return `<ul class="content sound-recognition-list">${items}</ul>`;
};

const generateWordChainContent = (section: Section): string => {
  const items = section.content
    .map(
      (item) =>
        `<span class="content-item">${extractTextContent(
          item.value as string,
        )}</span>`,
    )
    .join(' > ');
  return `<div class="content word-chain">${items}</div>`;
};

const generateVerticalListContent = (section: Section): string => {
  const items = section.content
    .map(
      (item) =>
        `<div class="content-item">${extractTextContent(
          item.value as string,
        )}</div>`,
    )
    .join('');
  return `<div class="content vertical-list">${items}</div>`;
};

const generateWordSumsContent = (section: Section): string => {
  const items = section.content
    .map((item) => {
      const textContent = extractTextContent(item.value as string);
      const [sum] = textContent.split('=');
      return `<div class="content-item">
        <span class="sum">${sum.trim()}</span> = <span class="answer-line">__________________</span>
      </div>`;
    })
    .join('');
  return `<div class="content word-sums">${items}</div>`;
};

const generateWorksheetLinesContent = (lineCount: number): string => {
  let lines = '';
  for (let i = 0; i < lineCount; i++) {
    lines += `<hr class="writing-line" />`;
  }
  return lines;
};

const generateOralInstructionContent = (): string => {
  return `<div class="content oral-instruction">
    <p>Complete this activity out loud. No writing necessary.</p>
  </div>`;
};

const generateWordMatrixContent = (section: Section): string => {
  const data = parseWordMatrixFromSection(section);

  const prefixColsHtml = data.prefixColumns
    .map((col) => {
      const items = col.map((p) => `<span class="prefix">${p}</span>`).join('');
      return `<div class="word-matrix-prefix-col">${items}</div>`;
    })
    .join('');
  const suffixColsHtml = data.suffixColumns
    .map((col) => {
      const items = col.map((s) => `<span class="suffix">${s}</span>`).join('');
      return `<div class="word-matrix-suffix-col">${items}</div>`;
    })
    .join('');

  const prefixColsSerialized = data.prefixColumns
    .map((col) => col.join(','))
    .join('|');
  const suffixColsSerialized = data.suffixColumns
    .map((col) => col.join(','))
    .join('|');

  return `
    <div class="word-matrix-content" data-base-word="${data.baseWord}" data-prefix-columns="${prefixColsSerialized}" data-suffix-columns="${suffixColsSerialized}">
      ${prefixColsHtml}
      <div class="word-matrix-base">${data.baseWord}</div>
      ${suffixColsHtml}
    </div>
    <p style="margin-top: 12px; font-size: 14px;"><strong>Make as many real words as you can:</strong></p>
    ${generateWorksheetLinesContent(5)}
  `;
};

function parseWordMatrixFromSection(section: Section): WordMatrixData {
  const baseWord =
    section.content
      .find((c) => (c.value as string).startsWith('base:'))
      ?.value?.toString()
      .replace('base:', '') || '';

  const prefixColsItem = section.content.find((c) =>
    (c.value as string).startsWith('prefixColumns:'),
  );
  const suffixColsItem = section.content.find((c) =>
    (c.value as string).startsWith('suffixColumns:'),
  );

  const parsePipeColumns = (raw: string): string[][] => {
    if (!raw) return [[]];
    return raw.split('|').map((col) => col.split(',').filter(Boolean));
  };

  const prefixColumns = prefixColsItem
    ? parsePipeColumns(
        (prefixColsItem.value as string).replace('prefixColumns:', ''),
      )
    : [[]];
  const suffixColumns = suffixColsItem
    ? parsePipeColumns(
        (suffixColsItem.value as string).replace('suffixColumns:', ''),
      )
    : [[]];

  return { baseWord, prefixColumns, suffixColumns };
}
